import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Component } from 'react';
/**
 * ErrorBoundary — catches React render errors and shows a recoverable fallback.
 *
 * Wraps each page individually so a crash in one page doesn't take down
 * the entire app. The sidebar and navigation remain functional.
 *
 * Usage:
 *   <ErrorBoundary pageName="Credit accounts">
 *     <CreditAccounts />
 *   </ErrorBoundary>
 *
 * Or use the withErrorBoundary() HOC for cleaner route definitions.
 */
export class ErrorBoundary extends Component {
    constructor() {
        super(...arguments);
        this.state = { hasError: false };
        this.handleReset = () => {
            this.setState({ hasError: false, error: undefined, errorInfo: undefined });
        };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, info) {
        // In production this would send to Sentry / Datadog
        console.error('[EarnPOS ErrorBoundary]', error, info.componentStack);
        this.setState({ errorInfo: info.componentStack });
    }
    render() {
        if (!this.state.hasError)
            return this.props.children;
        const { pageName = 'This page', error } = this.props;
        return (_jsxs("div", { className: "flex flex-col items-center justify-center h-full min-h-[400px] px-6 text-center", children: [_jsx("div", { className: "w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4", children: _jsx("i", { className: "ti ti-alert-triangle text-red-400 text-3xl", "aria-hidden": "true" }) }), _jsxs("h2", { className: "text-lg font-semibold text-gray-900 mb-1", children: [pageName, " ran into an error"] }), _jsx("p", { className: "text-sm text-gray-400 max-w-sm mb-6", children: "Something went wrong while loading this page. Your data is safe. You can try refreshing or navigate to a different section." }), error && (_jsxs("details", { className: "mb-6 text-left w-full max-w-md", children: [_jsx("summary", { className: "text-xs text-gray-400 cursor-pointer hover:text-gray-600 transition-colors mb-2", children: "Error details" }), _jsxs("pre", { className: "text-xs bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 overflow-x-auto text-red-700 font-mono leading-relaxed", children: [error.message, this.state.errorInfo && `\n\nComponent stack:${this.state.errorInfo.slice(0, 500)}…`] })] })), _jsxs("div", { className: "flex items-center gap-3", children: [_jsxs("button", { onClick: this.handleReset, className: "inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl transition-all shadow-ep active:scale-[0.98]", style: { background: '#1D6FEB' }, onMouseEnter: e => (e.currentTarget.style.background = '#1558C8'), onMouseLeave: e => (e.currentTarget.style.background = '#1D6FEB'), children: [_jsx("i", { className: "ti ti-refresh text-sm", "aria-hidden": "true" }), "Try again"] }), _jsxs("button", { onClick: () => window.location.href = '/dashboard', className: "inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all active:scale-[0.98]", children: [_jsx("i", { className: "ti ti-layout-dashboard text-sm", "aria-hidden": "true" }), "Go to dashboard"] })] })] }));
    }
}
/**
 * Higher-order component for clean route wrapping:
 *
 *   <Route path="/credit-accounts" element={withErrorBoundary(<CreditAccounts />, 'Credit accounts')} />
 */
export function withErrorBoundary(children, pageName) {
    return (_jsx(ErrorBoundary, { pageName: pageName, children: children }));
}
